/**
 * 维护模式管理工具
 * @description 检测系统是否处于维护状态，并根据状态进行跳转（Web 平台适配版）
 */

import axios from "axios";
import router from "@/router";
import global from "@/config/global";
import { getAuthToken } from "@/utils/auth";

const STORAGE_KEY = "isMaintenance";
const MAINTENANCE_PAGE = "/maintenance";
const MANAGE_PAGE = "/managet";
const LOGIN_PAGE = "/login";

/**
 * 检查当前是否在维护页面
 */
function isOnMaintenancePage() {
  return router.currentRoute.path === MAINTENANCE_PAGE;
}

/**
 * 获取运维配置 URL
 */
function getMaintenanceUrl() {
  const base = global.baseApi || "/";
  return base.replace(/\/$/, "") + "/ops.json";
}

/**
 * 跳转到维护页面
 * @param {string} errorMsg - 错误信息
 */
function redirectToMaintenance(errorMsg = "") {
  if (isOnMaintenancePage()) return;

  const query = errorMsg ? { errorMsg } : {};
  router.replace({
    path: MAINTENANCE_PAGE,
    query,
  });
}

/**
 * 处理维护状态
 * @param {Object} data - 接口返回的数据
 * @param {Function} onMaintenance - 维护模式回调
 * @param {Function} onNormal - 正常模式回调
 */
function handleMaintenanceStatus(data, onMaintenance, onNormal) {
  if (!data) {
    console.warn("[Maintenance] 数据为空");
    return;
  }

  if (
    data &&
    ((data.code && [502, 503, 599].includes(data.code)) || data.status === true)
  ) {
    // 开启维护
    localStorage.setItem(STORAGE_KEY, "true");
    redirectToMaintenance(data.message || data.msg || "");
    onMaintenance?.(data);
  } else {
    // 关闭维护
    localStorage.removeItem(STORAGE_KEY);
    if (isOnMaintenancePage()) {
      console.log("[Maintenance] 维护已结束，跳转对应页面");
      const target = getAuthToken() ? MANAGE_PAGE : LOGIN_PAGE;
      router.replace({ path: target });
    }
    onNormal?.(data);
  }
}

/**
 * 检测维护状态（主方法）
 * @param {Object} options
 * @param {Function} options.onSuccess - 请求成功回调
 * @param {Function} options.onFail - 请求失败回调
 * @param {Function} options.onMaintenance - 进入维护模式回调
 * @param {Function} options.onNormal - 恢复正常模式回调
 */
function checkMaintenance(options = {}) {
  const { errorMsg, onSuccess, onFail, onMaintenance, onNormal } = options;

  const url = getMaintenanceUrl();

  console.log("[Maintenance] 请求运维配置 URL:", url);

  axios
    .get(url, { timeout: 5000 })
    .then((res) => {
      console.log("[Maintenance] 获取运维配置成功:", res.data);

      if (res.data) {
        handleMaintenanceStatus(res.data, onMaintenance, onNormal);
      }

      onSuccess?.(res);
    })
    .catch((err) => {
      console.error("[Maintenance] 获取运维配置失败:", err);

      // 请求失败时的降级策略：如果本地有维护标记、或本次由 502/503/404 触发，且当前不在维护页，跳转维护页
      const isMaintenance = localStorage.getItem(STORAGE_KEY);
      if (isMaintenance && !isOnMaintenancePage()) {
        console.log("[Maintenance] 降级策略：跳转维护页");
        redirectToMaintenance(errorMsg);
      }

      onFail?.(err);
    });
}

/**
 * 清除维护状态（手动退出维护模式）
 */
function clearMaintenance() {
  localStorage.removeItem(STORAGE_KEY);
  if (isOnMaintenancePage()) {
    const target = getAuthToken() ? MANAGE_PAGE : LOGIN_PAGE;
    router.replace({ path: target });
  }
}

export {
  checkMaintenance,
  clearMaintenance,
  isOnMaintenancePage,
  getMaintenanceUrl,
  redirectToMaintenance,
  STORAGE_KEY,
};

export default {
  checkMaintenance,
  clearMaintenance,
  isOnMaintenancePage,
  getMaintenanceUrl,
  redirectToMaintenance,
  STORAGE_KEY,
};
